import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Col,
  CustomInput,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { PanelWembii } from "../PanelWembii";
import {
  InstagramAnalyticsSnapshot,
  InstagramAudienceSegment,
  InstagramHashtagRow,
  InstagramStatItem,
  InstagramTopContent,
} from "./instagramAnalyticsMock";
import "./InstagramAnalyticsDashboard.scss";

let sparkComboChartIdSeq = 0;

interface Props {
  snapshot: InstagramAnalyticsSnapshot;
  publicationsLoading?: {
    ranking: boolean;
    hashtags: boolean;
  };
}

const formatNumber = (value: number, unit?: string) => {
  if (unit === "%") return `${value.toFixed(1)}%`;
  return new Intl.NumberFormat("es-CL").format(value);
};

const formatPercent = (value: number) => `${value.toFixed(2).replace(".", ",")}%`;

const DONUT_SEGMENT_HEX = [
  "#0b65c2",
  "#38bdf8",
  "#22c55e",
  "#818cf8",
  "#0891b2",
  "#475569",
] as const;

const formatStat = (item: InstagramStatItem) =>
  item.unit === "%" ? formatPercent(item.value) : formatNumber(item.value);

const metricTabs = [
  "Evolución general",
  "Alcance / Visualizaciones",
  "Interacciones",
  "Actividad del perfil",
];

type SectionKey = "community" | "demographics" | "account" | "publications";
type ChartSeriesKey = "impressions" | "reach" | "interactions";
type ChartSeriesVisibility = Record<ChartSeriesKey, boolean>;

type PostListColumnKey =
  | "type"
  | "date"
  | "reach"
  | "views"
  | "interactions"
  | "likes"
  | "saves"
  | "shares"
  | "comments"
  | "engagement";

const POST_LIST_COLUMNS: Readonly<
  Record<PostListColumnKey, { header: string; menuLabel: string }>
> = {
  type: { header: "Tipo", menuLabel: "Tipo" },
  date: { header: "Fecha", menuLabel: "Fecha" },
  reach: { header: "Alcance orgánico", menuLabel: "Alcance orgánico" },
  views: { header: "Visualizaciones orgánicas", menuLabel: "Visualizaciones orgánicas" },
  interactions: { header: "Interacciones orgánicas", menuLabel: "Interacciones orgánicas" },
  likes: { header: "Likes orgánicos", menuLabel: "Likes orgánicos" },
  saves: { header: "Guardado orgánico", menuLabel: "Guardado orgánico" },
  shares: { header: "Compartidos orgánicos", menuLabel: "Compartidos orgánicos" },
  comments: { header: "Comentarios orgánicos", menuLabel: "Comentarios orgánicos" },
  engagement: { header: "Engagement orgánico", menuLabel: "Engagement orgánico" },
};

const DEFAULT_POST_LIST_VISIBILITY: Record<PostListColumnKey, boolean> = {
  type: true,
  date: true,
  reach: true,
  views: true,
  interactions: true,
  likes: true,
  saves: true,
  shares: true,
  comments: true,
  engagement: true,
};

const POST_THUMB_BACKGROUNDS = [
  "linear-gradient(145deg, #b91c1c 0%, #ea580c 55%, #fb923c 100%)",
  "linear-gradient(145deg, #9a3412 0%, #dc2626 50%, #f97316 100%)",
  "linear-gradient(145deg, #991b1b 0%, #c2410c 45%, #fdba74 100%)",
  "linear-gradient(145deg, #7f1d1d 0%, #ea580c 52%, #fbbf24 100%)",
];

const POST_LIST_COLUMN_KEYS = Object.keys(
  POST_LIST_COLUMNS
) as PostListColumnKey[];

const csvEscape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;

const buildSmoothPath = (coords: Array<{ x: number; y: number }>) => {
  if (coords.length === 0) return "";
  if (coords.length === 1) return `M ${coords[0].x} ${coords[0].y}`;

  const parts = [`M ${coords[0].x} ${coords[0].y}`];
  for (let i = 1; i < coords.length; i += 1) {
    const prev = coords[i - 1];
    const current = coords[i];
    const midX = (prev.x + current.x) / 2;
    parts.push(`C ${midX} ${prev.y}, ${midX} ${current.y}, ${current.x} ${current.y}`);
  }

  return parts.join(" ");
};

const downloadInstagramPostsCsv = (
  rows: InstagramTopContent[],
  visibility: Record<PostListColumnKey, boolean>
) => {
  const headers: string[] = ["Contenido"];
  POST_LIST_COLUMN_KEYS.filter((key) => visibility[key]).forEach((key) => {
    headers.push(POST_LIST_COLUMNS[key].header);
  });

  const body = rows.map((post) => {
    const cols: string[] = [csvEscape(post.title)];
    POST_LIST_COLUMN_KEYS.filter((key) => visibility[key]).forEach((key) => {
      switch (key) {
        case "type":
          cols.push(csvEscape(post.type));
          break;
        case "date":
          cols.push(csvEscape(`${post.date} ${post.time}`));
          break;
        case "reach":
          cols.push(String(post.reach));
          break;
        case "views":
          cols.push(String(post.views));
          break;
        case "interactions":
          cols.push(String(post.interactions));
          break;
        case "likes":
          cols.push(String(post.likes));
          break;
        case "saves":
          cols.push(String(post.saves));
          break;
        case "shares":
          cols.push(String(post.shares));
          break;
        case "comments":
          cols.push(String(post.comments));
          break;
        case "engagement":
          cols.push(`${post.engagementRate.toFixed(2)}%`);
          break;
        default:
          break;
      }
    });
    return cols.join(";");
  });

  const bom = "\ufeff";
  const blob = new Blob([bom + [headers.join(";"), ...body].join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `instagram-posts-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const postTypeIcon = (type: InstagramTopContent["type"]): IconProp => {
  switch (type) {
    case "Reel":
      return ["fas", "circle-play"];
    case "Carrusel":
      return ["fas", "clone"];
    case "Historia":
      return ["fas", "bolt"];
    default:
      return ["fas", "image"];
  }
};

const growthKpiIcon = (id: string): IconProp => {
  switch (id) {
    case "followers":
      return ["fas", "users"];
    case "following":
      return ["fas", "user-plus"];
    case "total-content":
      return ["fas", "images"];
    case "new-followers":
      return ["fas", "user-check"];
    default:
      return ["fas", "chart-line"];
  }
};

const growthDeltaTone = (
  item: InstagramStatItem
): "pos" | "neg" | "flat" => {
  if (item.direction === "down") return "neg";
  if (item.direction === "flat") return "flat";
  return "pos";
};

const StatGrid = ({
  title,
  items,
  compact = false,
}: {
  title: string;
  items: InstagramStatItem[];
  compact?: boolean;
}) => (
  <PanelWembii className="instagram-analytics__panel">
    <div className="instagram-analytics__panel-title">{title}</div>
    <div
      className={`instagram-analytics__stat-grid ${
        compact ? "instagram-analytics__stat-grid--compact" : ""
      }`}
    >
      {items.map((item) => (
        <div className="instagram-analytics__stat-item" key={item.id}>
          <div className="instagram-analytics__stat-value-row">
            <strong>{formatStat(item)}</strong>
            {item.direction && (
              <span
                className={`instagram-analytics__mini-trend instagram-analytics__mini-trend--${item.direction}`}
              >
                <FontAwesomeIcon
                  icon={[
                    "fas",
                    item.direction === "down"
                      ? "arrow-down"
                      : item.direction === "flat"
                      ? "minus"
                      : "arrow-up",
                  ]}
                />
              </span>
            )}
          </div>
          <span>{item.title}</span>
          {item.description && <small>{item.description}</small>}
        </div>
      ))}
    </div>
  </PanelWembii>
);

const AudienceList = ({
  title,
  items,
  actionLabel = "Ver tabla",
}: {
  title: string;
  items: InstagramAudienceSegment[];
  actionLabel?: string;
}) => (
  <PanelWembii className="instagram-analytics__panel">
    <div className="instagram-analytics__panel-heading instagram-analytics__panel-heading--compact">
      <div className="instagram-analytics__panel-title">{title}</div>
      <Button size="sm" outline className="instagram-analytics__ghost-button">
        <FontAwesomeIcon icon={["fas", actionLabel.includes("gráfico") ? "chart-bar" : "table"]} />
        {actionLabel}
      </Button>
    </div>
    <div className="instagram-analytics__audience-list">
      {items.length === 0 ? (
        <EmptyMetricState />
      ) : items.map((item) => (
        <div className="instagram-analytics__audience-item" key={item.label}>
          <div className="instagram-analytics__audience-label-row">
            <span>{item.label}</span>
            <strong>{item.percentage}%</strong>
          </div>
          <div className="instagram-analytics__progress">
            <div
              className="instagram-analytics__progress-bar"
              style={{ width: `${item.percentage}%` }}
            />
          </div>
          <small>{formatNumber(item.value)} cuentas</small>
        </div>
      ))}
    </div>
  </PanelWembii>
);

/** ~N evenly spaced day indices so the X axis does not wrap across dozens of rows. */
const INSTAGRAM_CHART_MAX_X_LABELS = 12;
const INSTAGRAM_CHART_SHOW_ALL_POINT_MARKERS_BELOW = 42;

const chartTickIndices = (
  pointCount: number,
  maxLabels = INSTAGRAM_CHART_MAX_X_LABELS
): number[] => {
  if (pointCount <= 0) return [];
  if (pointCount <= maxLabels) {
    return Array.from({ length: pointCount }, (_, i) => i);
  }
  const slots = maxLabels;
  const raw: number[] = [];
  for (let i = 0; i < slots; i++) {
    raw.push(Math.round((i * (pointCount - 1)) / Math.max(slots - 1, 1)));
  }
  return Array.from(new Set(raw)).sort((a, b) => a - b);
};

const DistributionCard = ({
  title,
  items,
}: {
  title: string;
  items: InstagramAudienceSegment[];
}) => (
  <PanelWembii className="instagram-analytics__panel">
    <div className="instagram-analytics__panel-title">{title}</div>
    <div className="instagram-analytics__distribution">
      {items.length === 0 ? (
        <EmptyMetricState />
      ) : items.map((item, index) => (
        <div className="instagram-analytics__distribution-row" key={item.label}>
          <span
            className={`instagram-analytics__distribution-dot instagram-analytics__distribution-dot--${index}`}
          />
          <span>{item.label}</span>
          <strong>{formatPercent(item.percentage)}</strong>
        </div>
      ))}
    </div>
  </PanelWembii>
);

const SparkComboChart = ({
  title,
  points,
  totalLabel,
  totalValue,
  activeMetricId,
  onMetricClick,
  seriesVisibility,
  kpiLayout = "floating",
  variant = "default",
  children,
}: {
  title: string;
  points: InstagramAnalyticsSnapshot["trend"];
  totalLabel?: string;
  totalValue?: number;
  activeMetricId?: string;
  onMetricClick?: (metricId: string) => void;
  seriesVisibility?: ChartSeriesVisibility;
  kpiLayout?: "floating" | "bento";
  variant?: "default" | "community";
  children?: React.ReactNode;
}) => {
  const gradientIds = useMemo(() => {
    const n = ++sparkComboChartIdSeq;
    return { reachArea: `igReachArea-${n}`, bar: `igBar-${n}` };
  }, []);
  const visibleSeries = seriesVisibility ?? {
    impressions: true,
    reach: true,
    interactions: true,
  };
  const xTickIndices = useMemo(
    () => chartTickIndices(points.length),
    [points.length]
  );
  const showPointMarker = (index: number) =>
    points.length <= INSTAGRAM_CHART_SHOW_ALL_POINT_MARKERS_BELOW ||
    xTickIndices.includes(index);
  const maxValue = Math.max(
    ...points.map((point) =>
      Math.max(
        visibleSeries.impressions ? point.impressions : 0,
        visibleSeries.reach ? point.reach : 0,
        visibleSeries.interactions ? point.interactions : 0
      )
    ),
    1
  );
  const width = 960;
  const height = 250;
  const leftPad = 44;
  const rightPad = 44;
  const topPad = 42;
  const bottomPad = 40;
  const chartHeight = height - topPad - bottomPad;
  const chartWidth = width - leftPad - rightPad;
  const xStep = chartWidth / Math.max(points.length - 1, 1);
  const barHalfWidth = Math.min(
    9,
    Math.max(1.25, (chartWidth / Math.max(points.length, 1)) * 0.42)
  );
  const baselineY = topPad + chartHeight;
  const reachCoords = points.map((point, index) => {
      const x = leftPad + index * xStep;
      const y = topPad + chartHeight - (point.reach / maxValue) * chartHeight;
      return { x, y };
    });
  const reachLinePath = buildSmoothPath(reachCoords);
  const firstReachX = leftPad;
  const lastReachX = leftPad + (points.length - 1) * xStep;
  const reachAreaPath = `${reachLinePath} L ${lastReachX} ${baselineY} L ${firstReachX} ${baselineY} Z`;
  const impressionsCoords = points.map((point, index) => {
      const x = leftPad + index * xStep;
      const y =
        topPad + chartHeight - (point.impressions / maxValue) * chartHeight;
      return { x, y };
    });
  const impressionsLinePath = buildSmoothPath(impressionsCoords);
  const interactionsCoords = points.map((point, index) => {
      const x = leftPad + index * xStep;
      const y =
        topPad + chartHeight - (point.interactions / maxValue) * chartHeight;
      return { x, y };
    });
  const interactionsLinePath = buildSmoothPath(interactionsCoords);

  return (
    <PanelWembii
      className={`instagram-analytics__chart-panel instagram-analytics__chart-panel--${variant}`}
    >
      <div className="instagram-analytics__chart-title-row">
        <div className="instagram-analytics__panel-title">{title}</div>
      </div>
      <div className="instagram-analytics__chart-meta-row">
        <div
          className={
            kpiLayout === "bento"
              ? "instagram-analytics__chart-kpis instagram-analytics__chart-kpis--bento"
              : "instagram-analytics__chart-kpis"
          }
        >
          {children}
        </div>
        {totalLabel !== undefined &&
          totalValue !== undefined && (
            <div className="instagram-analytics__chart-total">
              <strong>{formatNumber(totalValue)}</strong>
              <span>{totalLabel}</span>
            </div>
          )}
      </div>
      {points.length === 0 ? (
        <EmptyMetricState />
      ) : (
      <div className="instagram-analytics__svg-chart">
        {variant === "community" && (
          <div className="instagram-analytics__chart-legend">
            <span className="instagram-analytics__chart-legend-item instagram-analytics__chart-legend-item--followers">
              Seguidores
            </span>
            <span className="instagram-analytics__chart-legend-item instagram-analytics__chart-legend-item--following">
              Siguiendo
            </span>
            <span className="instagram-analytics__chart-legend-item instagram-analytics__chart-legend-item--content">
              Contenido total
            </span>
          </div>
        )}
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
          <defs>
            <linearGradient
              id={gradientIds.reachArea}
              x1="0"
              y1={topPad}
              x2="0"
              y2={baselineY}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.42" />
              <stop offset="70%" stopColor="#fb923c" stopOpacity="0.14" />
              <stop offset="100%" stopColor="#fed7aa" stopOpacity="0.03" />
            </linearGradient>
            <linearGradient id={gradientIds.bar} x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#ea580c" />
              <stop offset="100%" stopColor="#dc2626" />
            </linearGradient>
          </defs>
          {[0, 1, 2, 3].map((row) => (
            <line
              key={row}
              x1={leftPad}
              x2={width - rightPad}
              y1={topPad + (chartHeight / 3) * row}
              y2={topPad + (chartHeight / 3) * row}
              className="instagram-analytics__grid-line"
            />
          ))}
          {visibleSeries.impressions &&
            variant !== "community" &&
            points.map((point, index) => {
              const x = leftPad + index * xStep;
              const barHeight = Math.max(
                14,
                (point.impressions / maxValue) * chartHeight
              );
              return (
                <rect
                  key={point.label}
                  x={x - barHalfWidth}
                  y={topPad + chartHeight - barHeight}
                  width={barHalfWidth * 2}
                  height={barHeight}
                  rx={Math.min(9, barHalfWidth)}
                  fill={`url(#${gradientIds.bar})`}
                  className={`instagram-analytics__chart-bar ${
                    activeMetricId === "impressions" ? "active" : ""
                  }`}
                  onClick={() => onMetricClick?.("impressions")}
                />
              );
            })}
          {visibleSeries.impressions && variant === "community" && (
            <>
              <path
                d={impressionsLinePath}
                className={`instagram-analytics__chart-line instagram-analytics__chart-line--following ${
                  activeMetricId === "following" ? "active" : ""
                }`}
                onClick={() => onMetricClick?.("following")}
              />
              {points.map((point, index) => {
                if (!showPointMarker(index)) return null;
                const { x, y } = impressionsCoords[index];
                return (
                  <circle
                    key={`${point.label}-following`}
                    cx={x}
                    cy={y}
                    r="3"
                    className="instagram-analytics__chart-point instagram-analytics__chart-point--following"
                    onClick={() => onMetricClick?.("following")}
                  />
                );
              })}
            </>
          )}
          {visibleSeries.reach && (
            <>
              <path
                d={reachAreaPath}
                fill={`url(#${gradientIds.reachArea})`}
                stroke="none"
                className="instagram-analytics__chart-area-reach"
                pointerEvents="none"
              />
              <path
                d={reachLinePath}
                className={`instagram-analytics__chart-line ${
                  activeMetricId === "reach" ? "active" : ""
                }`}
                onClick={() => onMetricClick?.("reach")}
              />
              {points.map((point, index) => {
                if (!showPointMarker(index)) return null;
                const x = leftPad + index * xStep;
                const y =
                  topPad + chartHeight - (point.reach / maxValue) * chartHeight;
                return (
                  <circle
                    key={`${point.label}-point`}
                    cx={x}
                    cy={y}
                    r="5"
                    className="instagram-analytics__chart-point"
                    onClick={() => onMetricClick?.("reach")}
                  />
                );
              })}
            </>
          )}
          {visibleSeries.interactions && (
            <>
              <path
                d={interactionsLinePath}
                className="instagram-analytics__chart-line instagram-analytics__chart-line--secondary"
                onClick={() => onMetricClick?.("interactions")}
              />
              {points.map((point, index) => {
                if (!showPointMarker(index)) return null;
                const x = leftPad + index * xStep;
                const y =
                  topPad +
                  chartHeight -
                  (point.interactions / maxValue) * chartHeight;
                return (
                  <circle
                    key={`${point.label}-interactions`}
                    cx={x}
                    cy={y}
                    r="3"
                    className="instagram-analytics__chart-point instagram-analytics__chart-point--secondary"
                    onClick={() => onMetricClick?.("interactions")}
                  />
                );
              })}
            </>
          )}
        </svg>
        <div className="instagram-analytics__chart-labels">
          {xTickIndices.map((idx) => (
            <span key={`${points[idx].label}-${idx}`} title={points[idx].label}>
              {points[idx].label}
            </span>
          ))}
        </div>
      </div>
      )}
    </PanelWembii>
  );
};

const BalanceChart = ({
  points,
  total,
}: {
  points: InstagramAnalyticsSnapshot["followerBalanceDaily"];
  total: number;
}) => {
  const maxAbs = Math.max(...points.map((day) => Math.abs(day.net)), 1);
  const maxBarPx = 128;
  const balanceTickIndices = useMemo(
    () => chartTickIndices(points.length),
    [points.length]
  );

  return (
    <PanelWembii className="instagram-analytics__chart-panel instagram-analytics__balance-panel">
      <div className="instagram-analytics__chart-title-row instagram-analytics__balance-head">
        <div className="instagram-analytics__panel-title">Balance de seguidores</div>
        <div className="instagram-analytics__balance-total-chip">
          <strong>{formatNumber(total)}</strong>
          <span>Seguidores</span>
        </div>
      </div>
      {points.length === 0 ? (
        <EmptyMetricState />
      ) : (
      <div className="instagram-analytics__balance-chart instagram-analytics__balance-chart--dense">
        <div
          className="instagram-analytics__balance-chart-bars"
          style={
            {
              "--ig-balance-cols": points.length,
            } as React.CSSProperties
          }
        >
          {points.map((day, index) => {
            const negative = day.net < 0;
            const zero = day.net === 0;
            const heightPx = zero
              ? 5
              : Math.max(12, (Math.abs(day.net) / maxAbs) * maxBarPx);
            return (
              <div className="instagram-analytics__balance-column" key={`${day.label}-${index}`}>
                <div
                  className={`instagram-analytics__balance-bar ${
                    negative ? "instagram-analytics__balance-bar--negative" : ""
                  } ${zero ? "instagram-analytics__balance-bar--zero" : ""}`}
                  style={{ height: `${heightPx}px` }}
                  title={`${day.label} — ${day.net >= 0 ? "+" : ""}${day.net}`}
                />
                <span className="instagram-analytics__balance-day">
                  {balanceTickIndices.includes(index) ? day.label : "\u00a0"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </PanelWembii>
  );
};

const DonutCard = ({
  title,
  items,
}: {
  title: string;
  items: InstagramAudienceSegment[];
}) => {
  const gradient = `conic-gradient(${items
    .map((item, index) => {
      const start = items
        .slice(0, index)
        .reduce((sum, current) => sum + current.percentage, 0);
      const end = start + item.percentage;
      const color = DONUT_SEGMENT_HEX[index % DONUT_SEGMENT_HEX.length];
      return `${color} ${start}% ${end}%`;
    })
    .join(", ")})`;

  return (
    <PanelWembii className="instagram-analytics__panel instagram-analytics__donut-panel">
      <div className="instagram-analytics__panel-heading instagram-analytics__panel-heading--compact">
        <div className="instagram-analytics__panel-title">{title}</div>
        <Button size="sm" outline className="instagram-analytics__ghost-button">
          <FontAwesomeIcon icon={["fas", "table"]} />
          Ver tabla
        </Button>
      </div>
      {items.length === 0 ? (
        <EmptyMetricState />
      ) : (
      <div className="instagram-analytics__donut-layout">
        <div className="instagram-analytics__donut-cell">
          <div className="instagram-analytics__donut" style={{ background: gradient }}>
            <div className="instagram-analytics__donut-hole" />
          </div>
        </div>
        <ul className="instagram-analytics__donut-legend" aria-label="Leyenda del gráfico">
          {items.map((item, index) => {
            const fill = DONUT_SEGMENT_HEX[index % DONUT_SEGMENT_HEX.length];
            return (
              <li key={item.label} className="instagram-analytics__donut-legend-row">
                <span
                  className="instagram-analytics__donut-swatch"
                  style={{ backgroundColor: fill }}
                />
                <span className="instagram-analytics__donut-legend-label" title={item.label}>
                  {item.label}
                </span>
                <strong className="instagram-analytics__donut-legend-pct">
                  {formatPercent(item.percentage)}
                </strong>
              </li>
            );
          })}
        </ul>
      </div>
      )}
    </PanelWembii>
  );
};

const EmptyMetricState = () => (
  <div className="instagram-analytics__empty-state">
    <div>
      <FontAwesomeIcon icon={["fas", "chart-line"]} />
    </div>
    <strong>No hay datos disponibles</strong>
    <span>Los datos comenzarán a mostrarse cuando haya actividad disponible</span>
  </div>
);

const FloatingKpiButton = ({
  item,
  active,
  onClick,
}: {
  item: InstagramStatItem;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={`instagram-analytics__floating-kpi ${active ? "active" : ""}`}
    onClick={onClick}
  >
    <strong>{formatStat(item)}</strong>
    <span>{item.title}</span>
  </button>
);

const CommunityGrowthKpiCard = ({
  item,
  active,
  onClick,
}: {
  item: InstagramStatItem;
  active: boolean;
  onClick: () => void;
}) => {
  const deltaPct =
    item.change == null
      ? null
      : `${item.change > 0 ? "+" : ""}${item.change
          .toFixed(1)
          .replace(".", ",")}%`;
  const deltaIcon: IconProp =
    item.direction === "down"
      ? ["fas", "arrow-down"]
      : item.direction === "flat"
      ? ["fas", "minus"]
      : ["fas", "arrow-up"];
  const deltaTone = growthDeltaTone(item);

  return (
    <button
      type="button"
      className={`instagram-analytics__kpi-bento ${active ? "instagram-analytics__kpi-bento--active" : ""}`}
      onClick={onClick}
    >
      <div className="instagram-analytics__kpi-bento__head">
        <div className="instagram-analytics__kpi-bento__icon-wrap">
          <FontAwesomeIcon icon={growthKpiIcon(item.id)} fixedWidth />
        </div>
        {deltaPct != null ? (
          <span
            className={`instagram-analytics__kpi-bento__delta instagram-analytics__kpi-bento__delta--${deltaTone}`}
          >
            <FontAwesomeIcon icon={deltaIcon} />
            <span>{deltaPct}</span>
          </span>
        ) : null}
      </div>
      <div className="instagram-analytics__kpi-bento__body">
        <span className="instagram-analytics__kpi-bento__value">{formatStat(item)}</span>
        <span className="instagram-analytics__kpi-bento__label">{item.title}</span>
        {item.description ? (
          <span className="instagram-analytics__kpi-bento__desc">{item.description}</span>
        ) : null}
      </div>
    </button>
  );
};

const TopContentCard = ({ post }: { post: InstagramTopContent }) => (
  <div className="instagram-analytics__content-card">
    <div className="instagram-analytics__content-thumb">
      <FontAwesomeIcon
        icon={["fas", post.type === "Reel" ? "play" : "image"]}
      />
    </div>
    <div className="instagram-analytics__content-body">
      <div className="instagram-analytics__content-header">
        <Badge className="instagram-analytics__content-badge">
          {post.type}
        </Badge>
        <span>
          {post.date} · {post.time}
        </span>
        <span>{post.engagementRate.toFixed(1)}% engagement</span>
      </div>
      <strong>{post.title}</strong>
      <div className="instagram-analytics__content-stats">
        <span>{formatNumber(post.reach)} alcance</span>
        <span>{formatNumber(post.views)} visualizaciones</span>
        <span>{formatNumber(post.interactions)} interacciones</span>
        <span>{formatNumber(post.likes)} likes</span>
        <span>{formatNumber(post.saves)} guardados</span>
      </div>
    </div>
  </div>
);

const TableToolbar = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => (
  <div className="instagram-analytics__table-toolbar">
    <div className="instagram-analytics__search">
      <FontAwesomeIcon icon={["fas", "search"]} />
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </div>
  </div>
);

const PostsListToolbar = ({
  value,
  onChange,
  placeholder,
  visibility,
  onToggleColumn,
  onDownloadCsv,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  visibility: Record<PostListColumnKey, boolean>;
  onToggleColumn: (key: PostListColumnKey) => void;
  onDownloadCsv: () => void;
}) => {
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const leftKeys: PostListColumnKey[] = [
    "type",
    "date",
    "reach",
    "views",
    "interactions",
  ];
  const rightKeys: PostListColumnKey[] = [
    "likes",
    "saves",
    "shares",
    "comments",
    "engagement",
  ];

  return (
    <div className="instagram-analytics__table-toolbar instagram-analytics__posts-toolbar">
      <div className="instagram-analytics__search instagram-analytics__posts-search">
        <FontAwesomeIcon icon={["fas", "search"]} />
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
        />
      </div>
      <div className="instagram-analytics__table-actions">
        <Button size="sm" outline onClick={onDownloadCsv}>
          <FontAwesomeIcon icon={["fas", "file-csv"]} />
          Descargar CSV
        </Button>
        <Dropdown isOpen={columnsMenuOpen} toggle={() => setColumnsMenuOpen(!columnsMenuOpen)}>
          <DropdownToggle size="sm" outline caret={false}>
            <FontAwesomeIcon icon={["fas", "columns"]} />
            Columnas
          </DropdownToggle>
          <DropdownMenu
            end
            className="instagram-analytics__columns-menu"
          >
            <div className="instagram-analytics__columns-menu-grid">
              <div className="instagram-analytics__columns-menu-col">
                {leftKeys.map((key) => (
                  <label key={key} className="instagram-analytics__column-toggle">
                    <CustomInput
                      type="switch"
                      id={`ig-post-cols-${key}`}
                      checked={visibility[key]}
                      onChange={() => onToggleColumn(key)}
                    />
                    <span>{POST_LIST_COLUMNS[key].menuLabel}</span>
                  </label>
                ))}
              </div>
              <div className="instagram-analytics__columns-menu-col">
                {rightKeys.map((key) => (
                  <label key={key} className="instagram-analytics__column-toggle">
                    <CustomInput
                      type="switch"
                      id={`ig-post-cols-${key}`}
                      checked={visibility[key]}
                      onChange={() => onToggleColumn(key)}
                    />
                    <span>{POST_LIST_COLUMNS[key].menuLabel}</span>
                  </label>
                ))}
              </div>
            </div>
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
};

const renderPostListDataCell = (post: InstagramTopContent, key: PostListColumnKey) => {
  switch (key) {
    case "type":
      return (
        <span className="instagram-analytics__type-icon">
          <FontAwesomeIcon icon={postTypeIcon(post.type)} fixedWidth />
        </span>
      );
    case "date":
      return (
        <>
          <strong className="instagram-analytics__post-date">{post.date}</strong>
          <span className="instagram-analytics__post-time">{post.time}</span>
        </>
      );
    case "reach":
      return formatNumber(post.reach);
    case "views":
      return formatNumber(post.views);
    case "interactions":
      return formatNumber(post.interactions);
    case "likes":
      return formatNumber(post.likes);
    case "saves":
      return formatNumber(post.saves);
    case "shares":
      return formatNumber(post.shares);
    case "comments":
      return formatNumber(post.comments);
    case "engagement":
      return `${post.engagementRate.toFixed(2).replace(".", ",")}%`;
    default:
      return null;
  }
};

const postListDataCellClass = (key: PostListColumnKey) => {
  if (key === "type") return "instagram-analytics__td-type";
  if (key === "date") return "instagram-analytics__td-date";
  return "instagram-analytics__td-metric";
};

const PostsTable = ({
  posts,
  visibility,
}: {
  posts: InstagramTopContent[];
  visibility: Record<PostListColumnKey, boolean>;
}) => {
  const visibleKeys = POST_LIST_COLUMN_KEYS.filter((key) => visibility[key]);

  if (posts.length === 0) return <EmptyMetricState />;

  return (
    <div className="instagram-analytics__table-wrap instagram-analytics__posts-table-wrap">
      <table className="instagram-analytics__table instagram-analytics__table-posts">
        <thead>
          <tr>
            <th scope="col" className="instagram-analytics__th-post-preview">
              Contenido
            </th>
            {visibleKeys.map((key) => (
              <th scope="col" key={key} className="instagram-analytics__th-metric">
                {POST_LIST_COLUMNS[key].header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {posts.map((post, rowIdx) => {
            const thumbBg =
              POST_THUMB_BACKGROUNDS[rowIdx % POST_THUMB_BACKGROUNDS.length];

            return (
              <tr key={post.id}>
                <td
                  className={`instagram-analytics__post-preview-cell instagram-analytics__table-cell--start`}
                >
                  <div className="instagram-analytics__post-preview-row">
                    <div className="instagram-analytics__post-thumb" style={{ background: thumbBg }}>
                      <FontAwesomeIcon icon={postTypeIcon(post.type)} />
                    </div>
                    <div className="instagram-analytics__post-preview-text">
                      <p className="instagram-analytics__post-caption">{post.title}</p>
                      <div className="instagram-analytics__post-preview-strip">
                        {post.status === "Nuevo" && (
                          <span className="instagram-analytics__post-chip-new">{post.status}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="instagram-analytics__post-mini-metric"
                        aria-label={`Métricas de la publicación del ${post.date}`}
                      >
                        <FontAwesomeIcon icon={["fas", "chart-column"]} />
                      </button>
                    </div>
                    <UncontrolledDropdown direction="down">
                      <DropdownToggle
                        className="instagram-analytics__post-row-menu-toggle"
                        color="link"
                        caret={false}
                        aria-label="Acciones de la publicación"
                      >
                        <FontAwesomeIcon icon={["fas", "ellipsis-vertical"]} />
                      </DropdownToggle>
                      <DropdownMenu end>
                        <DropdownItem>Vista previa</DropdownItem>
                        <DropdownItem>Abrir métricas</DropdownItem>
                        <DropdownItem divider />
                        <DropdownItem>Copiar enlace</DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                </td>
                {visibleKeys.map((key, colIdx) => (
                  <td
                    key={`${post.id}-${key}`}
                    className={`${postListDataCellClass(key)} ${
                      colIdx === visibleKeys.length - 1
                        ? "instagram-analytics__table-cell--end"
                        : ""
                    }`}
                  >
                    {renderPostListDataCell(post, key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const HashtagsTable = ({ hashtags }: { hashtags: InstagramHashtagRow[] }) =>
  hashtags.length === 0 ? (
    <EmptyMetricState />
  ) : (
  <div className="instagram-analytics__table-wrap">
    <table className="instagram-analytics__table">
      <thead>
        <tr>
          <th>Hashtag</th>
          <th>Posts</th>
          <th>Alcance</th>
          <th>Interacciones</th>
          <th>Engagement</th>
        </tr>
      </thead>
      <tbody>
        {hashtags.map((row) => (
          <tr key={row.id}>
            <td className="instagram-analytics__table-title">{row.hashtag}</td>
            <td>{row.posts}</td>
            <td>{formatNumber(row.reach)}</td>
            <td>{formatNumber(row.interactions)}</td>
            <td>{formatPercent(row.engagementRate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AnalyticsSection = ({
  title,
  badge,
  children,
  isOpen,
  onToggle,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) => (
  <PanelWembii className="instagram-analytics__section">
    <button
      type="button"
      className="instagram-analytics__section-header"
      onClick={onToggle}
    >
      <span>
        {title}
        {badge && <Badge className="instagram-analytics__new-badge">{badge}</Badge>}
      </span>
      <FontAwesomeIcon icon={["fas", isOpen ? "chevron-up" : "chevron-down"]} />
    </button>
    {isOpen && <div className="instagram-analytics__section-body">{children}</div>}
  </PanelWembii>
);

export const InstagramAnalyticsDashboard = ({ snapshot, publicationsLoading }: Props) => {
  const [activeMetricTab, setActiveMetricTab] = useState("Evolución general");
  const [activeChartMetric, setActiveChartMetric] = useState("followers");
  const [communitySeriesVisibility, setCommunitySeriesVisibility] =
    useState<ChartSeriesVisibility>({
      impressions: false,
      reach: true,
      interactions: false,
    });
  const [postSearch, setPostSearch] = useState("");
  const [postListColumnVisibility, setPostListColumnVisibility] = useState<
    Record<PostListColumnKey, boolean>
  >({ ...DEFAULT_POST_LIST_VISIBILITY });
  const [postsPage, setPostsPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(5);
  const [hashtagSearch, setHashtagSearch] = useState("");
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    community: true,
    demographics: true,
    account: true,
    publications: true,
  });
  const sectionRefs = useRef<Record<SectionKey, HTMLDivElement | null>>({
    community: null,
    demographics: null,
    account: null,
    publications: null,
  });

  const filteredPosts = useMemo(
    () =>
      snapshot.topContent.filter((post) =>
        `${post.title} ${post.type} ${post.date}`
          .toLowerCase()
          .includes(postSearch.toLowerCase())
      ),
    [postSearch, snapshot.topContent]
  );

  useEffect(() => {
    setPostsPage(1);
  }, [postSearch, postsPerPage]);

  const postsMaxPage = Math.max(1, Math.ceil(filteredPosts.length / postsPerPage));

  useEffect(() => {
    setPostsPage((current) => Math.min(Math.max(1, current), postsMaxPage));
  }, [postsMaxPage]);

  const pagedPosts = useMemo(() => {
    const start = (postsPage - 1) * postsPerPage;
    return filteredPosts.slice(start, start + postsPerPage);
  }, [filteredPosts, postsPage, postsPerPage]);

  const postsRangeStart =
    filteredPosts.length === 0 ? 0 : (postsPage - 1) * postsPerPage + 1;
  const postsRangeEnd = Math.min(postsPage * postsPerPage, filteredPosts.length);

  const togglePostListColumn = (key: PostListColumnKey) => {
    setPostListColumnVisibility((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handlePostsCsv = () => {
    downloadInstagramPostsCsv(filteredPosts, postListColumnVisibility);
  };

  const filteredHashtags = useMemo(
    () =>
      snapshot.hashtags.filter((row) =>
        row.hashtag.toLowerCase().includes(hashtagSearch.toLowerCase())
      ),
    [hashtagSearch, snapshot.hashtags]
  );

  const scrollToSection = (section: SectionKey) => {
    window.setTimeout(() => {
      sectionRefs.current[section]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);
  };

  const toggleSection = (section: SectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
    scrollToSection(section);
  };

  const goToSectionNav = (section: SectionKey) => {
    setOpenSections((current) => ({ ...current, [section]: true }));
    scrollToSection(section);
  };

  const handleMetricClick = (metricId: string) => {
    setActiveChartMetric(metricId);
  };

  const handleMetricTabClick = (tab: string) => {
    setActiveMetricTab(tab);
  };

  const profileActivityPoints = snapshot.trend
    .filter((point) => point.profileActivity > 0)
    .map((point) => ({
      ...point,
      reach: point.profileActivity,
      impressions: point.profileActivity,
      interactions: point.profileActivity,
    }));
  const profileActivityTotal = snapshot.trend.reduce(
    (sum, point) => sum + point.profileActivity,
    0
  );

  const communitySeriesByMetric: Record<string, ChartSeriesKey> = {
    followers: "reach",
    following: "impressions",
    "total-content": "interactions",
  };

  const toggleCommunityMetric = (metricId: string) => {
    const series = communitySeriesByMetric[metricId];
    if (!series) {
      handleMetricClick(metricId);
      return;
    }
    handleMetricClick(metricId);
    setCommunitySeriesVisibility((current) => {
      const activeCount = Object.values(current).filter(Boolean).length;
      if (current[series] && activeCount === 1) return current;
      return { ...current, [series]: !current[series] };
    });
  };

  return (
    <div className="instagram-analytics instagram-analytics--ai">
      <div className="instagram-analytics__section-nav-wrap">
        <nav className="instagram-analytics__section-nav" aria-label="Secciones del informe">
          <button
            type="button"
            className="instagram-analytics__section-nav-item"
            onClick={() => goToSectionNav("community")}
          >
            Comunidad
          </button>
          <button
            type="button"
            className="instagram-analytics__section-nav-item"
            onClick={() => goToSectionNav("demographics")}
          >
            Demografía
          </button>
          <button
            type="button"
            className="instagram-analytics__section-nav-item"
            onClick={() => goToSectionNav("account")}
          >
            Cuenta
          </button>
          <button
            type="button"
            className="instagram-analytics__section-nav-item"
            onClick={() => goToSectionNav("publications")}
          >
            Publicaciones
          </button>
        </nav>
      </div>

      <div ref={(element) => (sectionRefs.current.community = element)}>
      <AnalyticsSection
        title="Comunidad"
        isOpen={openSections.community}
        onToggle={() => toggleSection("community")}
      >
        <Row>
          <Col xs="12" className="mb-3">
            <SparkComboChart
              title="Crecimiento"
              points={snapshot.trend}
              activeMetricId={activeChartMetric}
              onMetricClick={handleMetricClick}
              seriesVisibility={communitySeriesVisibility}
              kpiLayout="bento"
              variant="community"
            >
              {snapshot.growth.slice(0, 3).map((item) => (
                <CommunityGrowthKpiCard
                  key={item.id}
                  item={item}
                  active={communitySeriesVisibility[communitySeriesByMetric[item.id]]}
                  onClick={() => toggleCommunityMetric(item.id)}
                />
              ))}
            </SparkComboChart>
          </Col>
          <Col xs="12" className="mb-3">
            <StatGrid
              title="Indicadores de crecimiento"
              items={snapshot.followerBalance}
              compact
            />
          </Col>
          <Col xs="12">
            <BalanceChart
              points={snapshot.followerBalanceDaily}
              total={snapshot.growth[3].value}
            />
          </Col>
        </Row>
      </AnalyticsSection>
      </div>

      <div ref={(element) => (sectionRefs.current.demographics = element)}>
      <AnalyticsSection
        title="Demografía"
        isOpen={openSections.demographics}
        onToggle={() => toggleSection("demographics")}
      >
        <Row>
          <Col xs="12" md="6" xl="3" className="mb-3">
            <DistributionCard title="Género" items={snapshot.gender} />
          </Col>
          <Col xs="12" md="6" xl="3" className="mb-3">
            <DistributionCard title="Edad" items={snapshot.age} />
          </Col>
          <Col xs="12" md="6" xl="3" className="mb-3">
            <DonutCard title="Seguidores por país" items={snapshot.countries} />
          </Col>
          <Col xs="12" md="6" xl="3" className="mb-3">
            <AudienceList
              title="Seguidores por ciudad"
              items={snapshot.cities}
              actionLabel="Ver gráfico"
            />
          </Col>
        </Row>
      </AnalyticsSection>
      </div>

      <div ref={(element) => (sectionRefs.current.account = element)}>
      <AnalyticsSection
        title="Cuenta"
        isOpen={openSections.account}
        onToggle={() => toggleSection("account")}
      >
        <div className="instagram-analytics__metric-filter-row">
          <label>Grupo</label>
          <div className="instagram-analytics__metric-tabs">
            {metricTabs.map((tab) => (
              <button
                type="button"
                key={tab}
                className={tab === activeMetricTab ? "active" : ""}
                onClick={() => handleMetricTabClick(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
          <label>Métrica</label>
          <Input
            type="select"
            value={activeMetricTab}
            onChange={(event) => handleMetricTabClick(event.target.value)}
          >
            {metricTabs.map((tab) => (
              <option key={tab}>{tab}</option>
            ))}
          </Input>
        </div>
        <Row>
          <Col xs="12" xl="8" className="mb-3">
            {activeMetricTab === "Actividad del perfil" ? (
              <SparkComboChart
                title="Actividad del perfil"
                points={profileActivityPoints}
                totalLabel="Actividad"
                totalValue={profileActivityTotal}
                activeMetricId={activeChartMetric}
                onMetricClick={handleMetricClick}
              >
                <FloatingKpiButton
                  item={{
                    id: "profile-activity",
                    title: "Actividad del perfil",
                    value: profileActivityTotal,
                  }}
                  active={activeChartMetric === "profile-activity"}
                  onClick={() => handleMetricClick("profile-activity")}
                />
              </SparkComboChart>
            ) : (
              <SparkComboChart
                title={activeMetricTab}
                points={snapshot.trend}
                totalLabel="Visualizaciones"
                totalValue={snapshot.summary[1].value}
                activeMetricId={activeChartMetric}
                onMetricClick={handleMetricClick}
              >
                {snapshot.summary.map((metric) => (
                  <button
                    type="button"
                    className={`instagram-analytics__floating-kpi ${
                      activeChartMetric === metric.id ? "active" : ""
                    }`}
                    key={metric.id}
                    onClick={() => handleMetricClick(metric.id)}
                  >
                    <strong>{formatNumber(metric.value, metric.unit)}</strong>
                    <span>{metric.title}</span>
                  </button>
                ))}
              </SparkComboChart>
            )}
          </Col>
          <Col xs="12" xl="4" className="mb-3">
            <PanelWembii className="instagram-analytics__panel instagram-analytics__contract">
              <div className="instagram-analytics__panel-title">
                Conexión pendiente
              </div>
              <p>
                Algunas métricas pueden tener limitaciones por restricciones de Meta.
                Conecta una página de Facebook para acceso completo.
              </p>
              <Button color="primary" block className="mt-3">
                <FontAwesomeIcon icon={["fab", "facebook"]} />
                Conectar con Facebook
              </Button>
            </PanelWembii>
          </Col>
        </Row>
      </AnalyticsSection>
      </div>

      <div ref={(element) => (sectionRefs.current.publications = element)}>
      <AnalyticsSection
        title="Publicaciones"
        isOpen={openSections.publications}
        onToggle={() => toggleSection("publications")}
      >
        <Row>
          <Col xs="12" lg="6" className="mb-3">
            <StatGrid title="Resumen orgánico" items={snapshot.organicSummary} />
          </Col>
          <Col xs="12" lg="6" className="mb-3">
            <StatGrid
              title="Interacciones orgánicas"
              items={snapshot.organicInteractions}
              compact
            />
          </Col>
        </Row>
        <SparkComboChart
          title="Resumen orgánico"
          points={snapshot.trend}
          totalLabel="Posts"
          totalValue={snapshot.organicSummary[4].value}
          activeMetricId={activeChartMetric}
          onMetricClick={handleMetricClick}
        >
          {snapshot.organicSummary.map((item) => (
            <FloatingKpiButton
              key={item.id}
              item={item}
              active={activeChartMetric === item.id}
              onClick={() => handleMetricClick(item.id)}
            />
          ))}
        </SparkComboChart>
        <SparkComboChart
          title="Interacciones orgánicas"
          points={snapshot.trend}
          totalLabel="Me gusta"
          totalValue={snapshot.organicInteractions[0].value}
          activeMetricId={activeChartMetric}
          onMetricClick={handleMetricClick}
        >
          {snapshot.organicInteractions.slice(0, 4).map((item) => (
            <FloatingKpiButton
              key={item.id}
              item={item}
              active={activeChartMetric === item.id}
              onClick={() => handleMetricClick(item.id)}
            />
          ))}
        </SparkComboChart>
        <PanelWembii className="instagram-analytics__panel">
          <div className="instagram-analytics__panel-heading">
            <div>
              <div className="instagram-analytics__panel-title">
                Lista de posts
              </div>
            </div>
          </div>
          <PostsListToolbar
            value={postSearch}
            onChange={setPostSearch}
            placeholder="Buscar publicaciones"
            visibility={postListColumnVisibility}
            onToggleColumn={togglePostListColumn}
            onDownloadCsv={handlePostsCsv}
          />
          {publicationsLoading?.ranking ? (
            <div className="instagram-analytics__empty-state">
              <strong>Cargando publicaciones...</strong>
            </div>
          ) : (
            <PostsTable posts={pagedPosts} visibility={postListColumnVisibility} />
          )}
          <div className="instagram-analytics__pagination instagram-analytics__posts-pagination">
            <div className="instagram-analytics__posts-pagination-size">
              <label htmlFor="ig-posts-page-size">Elementos por página:</label>
              <Input
                id="ig-posts-page-size"
                type="select"
                bsSize="sm"
                className="instagram-analytics__posts-page-size-select"
                value={postsPerPage}
                onChange={(event) => setPostsPerPage(Number(event.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
              </Input>
            </div>
            <span className="instagram-analytics__posts-pagination-range">
              {filteredPosts.length === 0
                ? "0 de 0"
                : `${postsRangeStart}-${postsRangeEnd} de ${filteredPosts.length}`}
            </span>
            <div className="instagram-analytics__posts-pagination-nav" role="group" aria-label="Paginación">
              <Button
                size="sm"
                outline
                aria-label="Primera página"
                disabled={postsPage <= 1}
                onClick={() => setPostsPage(1)}
              >
                <FontAwesomeIcon icon={["fas", "angles-left"]} />
              </Button>
              <Button
                size="sm"
                outline
                aria-label="Página anterior"
                disabled={postsPage <= 1}
                onClick={() => setPostsPage((previous) => Math.max(1, previous - 1))}
              >
                <FontAwesomeIcon icon={["fas", "angle-left"]} />
              </Button>
              <Button
                size="sm"
                outline
                aria-label="Página siguiente"
                disabled={postsPage >= postsMaxPage}
                onClick={() =>
                  setPostsPage((previous) => Math.min(postsMaxPage, previous + 1))
                }
              >
                <FontAwesomeIcon icon={["fas", "angle-right"]} />
              </Button>
              <Button
                size="sm"
                outline
                aria-label="Última página"
                disabled={postsPage >= postsMaxPage}
                onClick={() => setPostsPage(postsMaxPage)}
              >
                <FontAwesomeIcon icon={["fas", "angles-right"]} />
              </Button>
            </div>
          </div>
        </PanelWembii>
        <PanelWembii className="instagram-analytics__panel mt-3">
          <div className="instagram-analytics__panel-heading">
            <div>
              <div className="instagram-analytics__panel-title">
                Lista de hashtags
              </div>
            </div>
          </div>
          <TableToolbar
            value={hashtagSearch}
            onChange={setHashtagSearch}
            placeholder="Buscar hashtags"
          />
          {publicationsLoading?.hashtags ? (
            <div className="instagram-analytics__empty-state">
              <strong>Cargando hashtags...</strong>
            </div>
          ) : (
            <HashtagsTable hashtags={filteredHashtags} />
          )}
        </PanelWembii>
        <PanelWembii className="instagram-analytics__panel mt-3">
          <div className="instagram-analytics__panel-heading">
            <div>
              <div className="instagram-analytics__panel-title">
                Ranking visual de contenidos
              </div>
            </div>
          </div>
          <div className="instagram-analytics__content-list">
            {publicationsLoading?.ranking ? (
              <div className="instagram-analytics__empty-state">
                <strong>Cargando ranking...</strong>
              </div>
            ) : filteredPosts.length === 0 ? (
              <EmptyMetricState />
            ) : (
              filteredPosts.map((post) => (
                <TopContentCard key={post.id} post={post} />
              ))
            )}
          </div>
        </PanelWembii>
      </AnalyticsSection>
      </div>
    </div>
  );
};
